import * as t from 'io-ts';
import {pick, uniq} from 'lodash';

import {TextCustomizationVariantsEnum} from '../../helpers/customization/textCustomizationVariants';
import {undefinedIfEmpty} from '../../helpers/utilities/objectHelpers';
import {makeApiSerializableType} from './apiTypes';
import {ChannelGroupsEnum} from './channelCommonTypes';
import {SandboxScenariosEnum} from './sandboxTypes';
import {parseTicketingPreferences, RTicketingPreferences} from './ticketingPreferencesModel';
import {makeSafeArrayType} from './types/arrayTypes';
import {makeDefaultValueType} from './types/defaultValueType';
import {makeEnumRuntimeType, makeSafeEnumRuntimeType, makeStrictEnumRuntimeType} from './types/enumTypes';
import {makeOptional} from './types/optionalType';

/*
 * Runtime type.
 */

export enum BumpStrategiesEnum {
  NEVER = 'never',
  MESSAGE = 'outbound',
  COMMENT = 'comment',
  MESSAGE_AND_COMMENT = 'always',
}

export enum DefaultTeammateAccessRolesEnum {
  NONE = 'none',
  FREE_BUSY = 'free-busy',
  READER = 'reader',
}

export enum CompanyAutoContactCreationValuesEnum {
  NEVER = 'never',
  INBOUND = 'inbound',
  OUTBOUND = 'outbound',
  ALWAYS = 'always',
}

export enum CompanyContactsScopeValuesEnum {
  NONE = 'none',
  DOUBLE_WRITE = 'double_write',
  READ_WRITE = 'read_write',
}

export enum CompanySsoModesEnum {
  SAML = 'saml',
  OAUTH = 'oauth',
  DISABLED = 'disabled',
}

export enum CompanySessionIdleTimeoutsEnum {
  NEVER = 0,
  MINUTES_15 = 900000,
  MINUTES_30 = 1800000,
  HOURS_1 = 3600000,
  HOURS_2 = 7200000,
  HOURS_4 = 14400000,
  HOURS_8 = 28800000,
  HOURS_12 = 43200000,
  HOURS_24 = 86400000,
}

export enum CompanySsoSamlAuthnRequestBindingsEnum {
  HTTP_REDIRECT = 'HTTP-Redirect',
  HTTP_POST = 'HTTP-POST',
}

export enum CompanySsoSamlAuthnContextsEnum {
  PASSWORD_PROTECTED_TRANSPORT = 'urn:oasis:names:tc:SAML:2.0:ac:classes:PasswordProtectedTransport',
  DISABLED = '',
}

export enum CompanyPainPointsEnum {
  EMAIL_OVERLOAD = 'email overload',
  SLOW_RESPONSE_TIME = 'slow response time',
  MISSED_EMAILS = 'missed emails',
  DUPLICATE_EMAILS = 'duplicate emails',
  FRUSTRATED_CUSTOMERS = 'frustrated customers',
  INCOMPLETE_VIEW_OF_CUSTOMER_COMMS = 'incomplete view of customer comms',
  INEFFICIENT_NON_EXISTANT_COLLABORATION = 'inefficient/non-existant collaboration',
  SILOED_EMAILS = 'siloed emails',
  LACK_OF_OWNERSHIP = 'lack of ownership',
  LACK_OF_VISIBILITY = 'lack of visibility',
  OOO_HANDLING = 'OOO handling',
  LACK_OF_EMAIL_CATEGORIZATION = 'lack of email categorization',
  POOR_NOTIFICATIONS = 'poor notifications',
  NO_SLA_RESPONSE_TIME_TRACKING = 'no SLA/response time tracking',
  NO_SENSE_OF_ROI_FOR_COMM = 'no sense of ROI for comms',
}

export enum PermalinkAccessEnum {
  ALL_TEAMMATES = 'all_teammates',
  PARTICIPANTS_ONLY = 'participants_only',
}

export enum WorkspacePermissionsMigrationStatusEnum {
  NOT_MIGRATED = 'not_migrated',
  IN_PROGRESS = 'in_progress',
  MIGRATED = 'migrated',
}

export const RPainPoints = makeEnumRuntimeType(CompanyPainPointsEnum);

export enum AdminOnboardingResourcesTopicsStatus {
  PENDING = 'pending',
  COMPLETE = 'complete',
  UNKNOWN = 'unknown',
}

const RAdminOnboardingResources = t.type({
  circleId: t.union([t.number, t.null]),
  inboxId: t.union([t.number, t.null]),
  inboxIds: t.union([makeSafeArrayType(t.number), t.null]),
  workflowInboxIds: t.union([makeSafeArrayType(t.number), t.null]),
  channelIds: t.union([makeSafeArrayType(t.number), t.null]),
  namespace: t.union([t.string, t.null]),
  goals: t.union([makeSafeArrayType(t.string), t.null]),
  topicsStatus: t.union([
    makeSafeEnumRuntimeType(
      AdminOnboardingResourcesTopicsStatus,
      AdminOnboardingResourcesTopicsStatus.UNKNOWN,
    ),
    t.null,
  ]),
  topicsTagIds: t.union([makeSafeArrayType(t.number), t.null]),
  topicsGenerationStartedAt: makeOptional(t.number),
});

export enum OnboardingExperiencesEnum {
  CUSTOMER_SUPPORT = 'customer_support',
  LOGISTICS = 'logistics',
}

const RCompanyActivationPreferences = t.type({
  adminOnboardingResources: RAdminOnboardingResources,
  onboardingExperience: makeSafeArrayType(makeStrictEnumRuntimeType(OnboardingExperiencesEnum)),
  isOnboardingSetupGuideDisabled: makeDefaultValueType(t.boolean, false),
});

const RCsatSurveySettingsPreferences = t.type({
  primaryColor: t.string,
  secondaryColor: t.string,
  primaryButtonText: makeSafeEnumRuntimeType(
    TextCustomizationVariantsEnum,
    TextCustomizationVariantsEnum.LIGHT,
  ),
  surveyPrompt: t.string,
  answerFieldPlaceholderText: t.string,
  buttonText: t.string,
  shouldDisplayPoweredByFront: t.boolean,
  companyLogo: makeOptional(
    t.type({
      name: t.string,
      path: t.string,
      url: t.string,
      cid: t.string,
      contentType: t.string,
      size: t.number,
    }),
  ),
});

export const RAiSuggestedReplyEnabledFilter = t.partial({
  namespace: t.string,
  isSuggestedReplyEnabled: t.boolean,
});

export enum AiSynopsisFilterknowledgeSourceTypesEnum {
  KNOWLEDGE_BASE = 'knowledge_base',
  KNOWLEDGE_SOURCE = 'knowledge_source',
}

const RAiSynopsisFilter = makeSafeArrayType(
  t.intersection([
    RAiSuggestedReplyEnabledFilter,
    t.type({
      filter: t.partial({
        allShared: t.boolean,
        includeIds: makeSafeArrayType(t.number),
        excludeIds: makeSafeArrayType(t.number),
      }),
      knowledgeSources: makeSafeArrayType(
        t.type({
          id: t.number,
          type: t.union([t.literal('knowledge_base'), t.literal('knowledge_source')]),
        }),
      ),
    }),
  ]),
);

const RAiSettingsPreferences = t.partial(
  t.type({
    draftArticle: t.type({
      isEnabled: t.boolean,
    }),
    compose: t.type({
      isEnabled: t.boolean,
    }),
    internalAiAnswers: t.type({
      isEnabled: t.boolean,
    }),
    translate: t.type({
      isEnabled: t.boolean,
    }),
    synopsis: t.type({
      isEnabled: t.boolean,
      filters: RAiSynopsisFilter,
    }),
    autopilot: t.type({
      filters: makeSafeArrayType(
        t.type({
          namespace: t.string,
          disclosure: t.type({
            async_enabled: t.boolean,
            sync_enabled: t.boolean,
          }),
        }),
      ),
    }),
  }).props,
);

export const RCompanyPreferences = t.readonly(
  t.type({
    administrativeContacts: t.type({
      billing: t.array(t.string),
    }),
    autoContactCreation: makeEnumRuntimeType(CompanyAutoContactCreationValuesEnum),
    canMoveToInaccessibleInbox: t.boolean,
    customFieldsPerResourceLimit: t.number,
    everyoneCanBlockSender: t.boolean,
    permalinkAccess: makeEnumRuntimeType(PermalinkAccessEnum),
    ssoMode: makeEnumRuntimeType(CompanySsoModesEnum),
    saml: t.partial({
      entryPoint: t.string,
      authnRequestBinding: makeEnumRuntimeType(CompanySsoSamlAuthnRequestBindingsEnum),
      authnContext: t.union([makeEnumRuntimeType(CompanySsoSamlAuthnContextsEnum), t.null]),
      signingCertificate: t.string,
    }),
    hasScimDisableEdit: t.boolean,
    hasInternalDiscussions: t.boolean,
    shouldLimitRecipientsDuringTrial: t.boolean,
    sendBumpStrategy: makeEnumRuntimeType(BumpStrategiesEnum),
    painPoints: makeSafeArrayType(RPainPoints),
    hasAutocompleteVisitorContacts: t.boolean,
    isInvitingNewVisitorsEnabled: t.boolean,
    isChannelAbuser: t.boolean,
    isUserAllowedToEnableIntegrations: t.boolean,
    includeFrontBrandingInCalendarInvites: t.boolean,
    canEditComments: t.boolean,
    shouldReplyWithOutlookStyle: t.boolean,
    sessionIdleTimeout: makeEnumRuntimeType(CompanySessionIdleTimeoutsEnum),
    defaultTeammateAccessRole: makeEnumRuntimeType(DefaultTeammateAccessRolesEnum),
    companySize: makeOptional(t.Integer),
    maxTwitterChannels: t.number,
    numAddonChannels: t.number,
    customerSuccessManagerEmail: makeOptional(t.string),
    isCustomPricingAlertsDisabled: t.boolean,
    isBillingHidden: t.boolean,
    isDataDeletionEnabled: t.boolean,
    allowInboxLevelDataDeletion: t.boolean,
    trashConversationsCutoffDays: makeOptional(t.Integer),
    isDataDeletionAutoDeleteConversationsEnabled: t.boolean,
    dataDeletionEmptyTrashCutoffDays: t.Integer,
    isDataDeletionAutoDeleteContactsEnabled: t.boolean,
    // company contacts were renamed to shared contacts on FE Nov 2022
    companyContactsScope: makeEnumRuntimeType(CompanyContactsScopeValuesEnum),
    maxTeammatesDuringTrial: t.Integer,
    salesforceAmEmail: t.union([t.string, t.null]),
    skipSpfDkimWarnings: t.boolean,
    hasScimGroupSync: t.boolean,
    delayedOnboarding: t.boolean,
    isAiComposeEnabled: t.boolean,
    isAiDraftArticleEnabled: t.boolean,
    isAiSummarizeEnabled: t.boolean,
    isAiInternalAnswersEnabled: t.boolean,
    isAiTranslateEnabled: t.boolean,
    isAiSynopsisEnabled: t.boolean,
    isAiEchoesEnabled: t.boolean,
    namespacesWithEchoes: makeSafeArrayType(t.string),
    isAutoJoinEnabled: t.boolean,
    isAutoClaimDomainsEnabled: t.boolean,
    overrideChannelsTeammatesRatio: makeOptional(t.number),
    limited2WaySyncForEmailChannels: t.boolean,
    sandboxCompletedScenarios: makeSafeArrayType(t.string),
    rateLimit: makeOptional(t.number),
    trialEndedFeatures: makeOptional(makeSafeArrayType(t.string)),
    trialFeatures: makeOptional(makeSafeArrayType(t.string)),
    markedAsSpammer: t.boolean,
    activationPreferences: RCompanyActivationPreferences,
    enableContactPermissions: t.boolean,
    csatSurveySettings: RCsatSurveySettingsPreferences,
    companyEnforcedSignatures: t.boolean,
    ticketing: makeOptional(RTicketingPreferences),
    shouldMigrateNewGmailChannels: t.boolean,
    ticketStatusLimit: t.number,
    selectedChannelGroup: makeOptional(
      makeSafeEnumRuntimeType(ChannelGroupsEnum, ChannelGroupsEnum.FALLBACK_UNKNOWN),
    ),
    emailThreadingVersion: t.number,
    aiSettings: makeOptional(RAiSettingsPreferences),
    shouldShowWorkspaceIndicatorInCell: makeDefaultValueType(t.boolean, false),
    shouldShowInboxIndicatorInCell: makeDefaultValueType(t.boolean, false),
    workspacePermissionsMigrationStatus: makeSafeEnumRuntimeType(
      WorkspacePermissionsMigrationStatusEnum,
      WorkspacePermissionsMigrationStatusEnum.NOT_MIGRATED,
    ),
  }),
);

/*
 * Type.
 */

export interface AdminOnboardingResources extends t.TypeOf<typeof RAdminOnboardingResources> {}
export interface CompanyActivationPreferences extends t.TypeOf<typeof RCompanyActivationPreferences> {}
export interface CustomerSatisfactionSurveySettingsPreferences
  extends t.TypeOf<typeof RCsatSurveySettingsPreferences> {}
export interface CompanyPreferencesModel extends t.TypeOf<typeof RCompanyPreferences> {}

/*
 * IO methods.
 */

export function parseCompanyPreferences(src: any): CompanyPreferencesModel {
  return {
    administrativeContacts: {
      billing: src.administrativeContacts?.billing || [],
    },
    autoContactCreation: src.autoContactCreation || CompanyAutoContactCreationValuesEnum.OUTBOUND,
    customFieldsPerResourceLimit: src.customFieldsPerResourceLimit || 50,
    canMoveToInaccessibleInbox: Boolean(src.canMoveToInaccessibleInbox),
    permalinkAccess: src.permalinkAccess || PermalinkAccessEnum.PARTICIPANTS_ONLY,
    everyoneCanBlockSender: Boolean(src.everyoneCanBlockSender),
    painPoints: src.hypothesizedPains || [],
    ssoMode: parseSsoMode(src),
    saml: {
      entryPoint: src.sso?.saml?.entryPoint,
      authnRequestBinding: src.sso?.saml?.authnRequestBinding,
      authnContext: src.sso?.saml?.authnContext,
      signingCertificate: src.sso?.saml?.signingCertificate,
    },
    hasScimDisableEdit: Boolean(src.scim && src.scim.disableEdit),
    hasScimGroupSync: Boolean(src.scim && src.scim.groupSync),
    shouldLimitRecipientsDuringTrial: Boolean(src.limitRecipientsDuringTrial),
    hasInternalDiscussions: Boolean(src.discussion),
    sendBumpStrategy: src.bumpOnCommentAndOutbound,
    hasAutocompleteVisitorContacts: Boolean(src.autocompleteMentionContacts),
    isInvitingNewVisitorsEnabled: Boolean(src.supportsVisitors),
    isChannelAbuser: Boolean(src.isChannelAbuser),
    isUserAllowedToEnableIntegrations: Boolean(src.isUserAllowedToEnableIntegrations),
    includeFrontBrandingInCalendarInvites: Boolean(src.includeFrontBrandingInCalendarInvites),
    canEditComments: Boolean(src.canEditComments),
    shouldReplyWithOutlookStyle: Boolean(src.outlookStyleReply),
    sessionIdleTimeout: src.sessionIdleTimeout || CompanySessionIdleTimeoutsEnum.NEVER,
    defaultTeammateAccessRole: src.defaultTeammateCalendarAccessRole || DefaultTeammateAccessRolesEnum.READER,
    companySize: src.companySize || undefined,
    maxTwitterChannels: src.maxTwitterChannels ?? 0,
    numAddonChannels: src.numAddonChannels ?? 0,
    customerSuccessManagerEmail: src.salesforceCsmEmail || undefined,
    isCustomPricingAlertsDisabled: Boolean(src.disableCustomPricingAlerts),
    isBillingHidden: Boolean(src.hideBilling),
    isDataDeletionEnabled: Boolean(src.isDataRetentionEnabled), // Initially called "retention" in BE.
    isDataDeletionAutoDeleteConversationsEnabled: Boolean(src.isDataRetentionAutoDeleteConversationsEnabled), // Initially called "retention" in BE.
    isDataDeletionAutoDeleteContactsEnabled: Boolean(src.isDataRetentionAutoDeleteContactsEnabled), // Initially called "retention" in BE.
    allowInboxLevelDataDeletion: Boolean(src.allowInboxLevelDataRetention),
    trashConversationsCutoffDays: src.trashConversationsCutoffDays || undefined,
    dataDeletionEmptyTrashCutoffDays: src.dataRetentionEmptyTrashCutoffDays || 7, // Initially called "retention" in BE.
    companyContactsScope: src.companyContactsScope || CompanyContactsScopeValuesEnum.NONE,
    maxTeammatesDuringTrial: src.maxTeammatesDuringTrial || 10,
    salesforceAmEmail: src.salesforceAmEmail || null,
    skipSpfDkimWarnings: src.skipSpfDkimWarnings ?? false,
    delayedOnboarding: Boolean(src.delayedOnboarding),
    isAiComposeEnabled: Boolean(src.aiFeatures?.compose?.isEnabled),
    isAiDraftArticleEnabled: Boolean(src.aiFeatures?.draftArticle?.isEnabled),
    isAiInternalAnswersEnabled: Boolean(src.aiFeatures?.internalAiAnswers?.isEnabled),
    isAiSummarizeEnabled: Boolean(src.aiFeatures?.summarize?.isEnabled),
    isAiTranslateEnabled: Boolean(src.aiFeatures?.translate?.isEnabled),
    isAiSynopsisEnabled: Boolean(src.aiFeatures?.synopsis?.isEnabled),
    namespacesWithEchoes: src.aiFeatures?.namespacesWithEchoes ?? [],
    isAiEchoesEnabled: Boolean(src.aiFeatures?.isAiEchoesEnabled),
    isAutoJoinEnabled: Boolean(src.isAutojoinEnabled),
    isAutoClaimDomainsEnabled: Boolean(src.isAutoClaimDomainsEnabled),
    overrideChannelsTeammatesRatio: src.overrideChannelsTeammatesRatio,
    limited2WaySyncForEmailChannels: Boolean(src.limited2WaySyncForEmailChannels),
    sandboxCompletedScenarios: parseSandboxCompletedScenarios(src),
    activationPreferences: parseActivationPreferences(src),
    rateLimit: src.rateLimit || undefined,
    trialEndedFeatures: src.trialEndedFeatures || [],
    trialFeatures: src.trialFeatures || [],
    markedAsSpammer: Boolean(src.markedAsSpammer),
    enableContactPermissions: Boolean(src.enableContactPermissions),
    csatSurveySettings: parseCsatSurveySettings(src),
    companyEnforcedSignatures: Boolean(src.companyEnforcedSignatures ?? false),
    ticketing: src.ticketing ? parseTicketingPreferences(src.ticketing) : undefined,
    shouldMigrateNewGmailChannels: Boolean(src.shouldMigrateNewGmailChannels),
    // The fallback here should not be necessary, but we don't want to break
    // boot or company push updates that omit the field, so let's have a fallback.
    ticketStatusLimit: src.ticketStatusLimit || 10,
    selectedChannelGroup: src.selectedChannelGroup ?? undefined,
    emailThreadingVersion: src.emailThreadingVersion ?? 1,
    aiSettings: src.aiFeatures,
    shouldShowWorkspaceIndicatorInCell: Boolean(src.shouldShowWorkspaceIndicatorInCell),
    shouldShowInboxIndicatorInCell: Boolean(src.shouldShowInboxIndicatorInCell),
    workspacePermissionsMigrationStatus:
      src.workspacePermissionsMigrationStatus || WorkspacePermissionsMigrationStatusEnum.NOT_MIGRATED,
  };
}

function parseSandboxCompletedScenarios(src: any): ReadonlyArray<string> {
  // Parse the legacy company preference - uses SCENARIO3_ACT2 (without AI compose) for backward compatibility
  const legacyCompletedScenarios = src.sandboxIsCompleted ? [SandboxScenariosEnum.SCENARIO3_ACT2] : [];

  // Parse the new company preference.
  const completedScenariosSrc = src.sandbox?.completedScenarios;
  const completedScenarios = Array.isArray(completedScenariosSrc) ? completedScenariosSrc : [];

  return uniq([...completedScenarios, ...legacyCompletedScenarios]);
}

function parseActivationPreferences(src: any) {
  return {
    adminOnboardingResources: {
      circleId: null,
      inboxId: null,
      inboxIds: null,
      workflowInboxIds: null,
      channelIds: null,
      namespace: null,
      goals: null,
      topicsStatus: null,
      topicsTagIds: null,
      ...src.activationPreferences?.adminOnboardingResources,
    },
    onboardingExperience: src.activationPreferences?.onboardingExperience ?? [],
    isOnboardingSetupGuideDisabled: Boolean(src.activationPreferences?.isOnboardingSetupGuideDisabled),
  };
}

const defaultCsatSurveySettings: CustomerSatisfactionSurveySettingsPreferences = {
  primaryColor: '#000000',
  secondaryColor: '#FFFFFF',
  primaryButtonText: TextCustomizationVariantsEnum.LIGHT,
  surveyPrompt: 'Please provide your feedback',
  answerFieldPlaceholderText: 'Tell us more',
  buttonText: 'Submit',
  shouldDisplayPoweredByFront: true,
  companyLogo: undefined,
};

function parseCsatSurveySettings(src: any) {
  const {csatSurveySettings} = src;

  return {
    ...defaultCsatSurveySettings,
    ...pick(csatSurveySettings, Object.keys(defaultCsatSurveySettings)),
  };
}

function parseSsoMode(src: any) {
  if (src.enforceGoogleOrOffice365Login) {
    return CompanySsoModesEnum.OAUTH;
  }

  if (src.sso?.enabled) {
    return CompanySsoModesEnum.SAML;
  }

  return CompanySsoModesEnum.DISABLED;
}

export function serializeCompanyPreferences(companyPreferences: Partial<CompanyPreferencesModel>): any {
  return {
    autoContactCreation: companyPreferences.autoContactCreation,
    bumpOnCommentAndOutbound: companyPreferences.sendBumpStrategy,
    canMoveToInaccessibleInbox: companyPreferences.canMoveToInaccessibleInbox,
    permalinkAccess: companyPreferences.permalinkAccess,
    everyoneCanBlockSender: companyPreferences.everyoneCanBlockSender,
    sso: companyPreferences.ssoMode && {
      enabled: companyPreferences.ssoMode === CompanySsoModesEnum.SAML,
      saml: companyPreferences.ssoMode === CompanySsoModesEnum.SAML ? companyPreferences.saml : null,
    },
    enforceGoogleOrOffice365Login:
      companyPreferences.ssoMode && companyPreferences.ssoMode === CompanySsoModesEnum.OAUTH,
    hasInternalDiscussions: companyPreferences.hasInternalDiscussions,
    shouldLimitRecipientsDuringTrial: companyPreferences.shouldLimitRecipientsDuringTrial,
    painPoints: companyPreferences.painPoints,
    hasAutocompleteVisitorContacts: companyPreferences.hasAutocompleteVisitorContacts,
    isInvitingNewVisitorsEnabled: companyPreferences.isInvitingNewVisitorsEnabled,
    isUserAllowedToEnableIntegrations: companyPreferences.isUserAllowedToEnableIntegrations,
    includeFrontBrandingInCalendarInvites: companyPreferences.includeFrontBrandingInCalendarInvites,
    canEditComments: companyPreferences.canEditComments,
    shouldReplyWithOutlookStyle: companyPreferences.shouldReplyWithOutlookStyle,
    sessionIdleTimeout: companyPreferences.sessionIdleTimeout,
    defaultTeammateCalendarAccessRole: companyPreferences.defaultTeammateAccessRole,
    companySize: companyPreferences.companySize,
    customerSuccessManagerEmail: companyPreferences.customerSuccessManagerEmail,
    isCustomPricingAlertsDisabled: companyPreferences.isCustomPricingAlertsDisabled,
    isBillingHidden: companyPreferences.isBillingHidden,
    isDataRetentionEnabled: companyPreferences.isDataDeletionEnabled, // Initially called "retention" in BE.
    allowInboxLevelDataRetention: companyPreferences.allowInboxLevelDataDeletion, // Initially called "retention" in BE.
    trashConversationsCutoffDays: companyPreferences.trashConversationsCutoffDays, // Initially called "retention" in BE.
    isDataRetentionAutoDeleteConversationsEnabled:
      companyPreferences.isDataDeletionAutoDeleteConversationsEnabled,
    dataRetentionEmptyTrashCutoffDays: companyPreferences.dataDeletionEmptyTrashCutoffDays, // Initially called "retention" in BE.
    isDataRetentionAutoDeleteContactsEnabled: companyPreferences.isDataDeletionAutoDeleteContactsEnabled,
    companyContactsScope: companyPreferences.companyContactsScope,
    maxTeammatesDuringTrial: companyPreferences.maxTeammatesDuringTrial,
    salesforceAmEmail: companyPreferences.salesforceAmEmail,
    skipSpfDkimWarnings: companyPreferences.skipSpfDkimWarnings,
    scim: undefinedIfEmpty({
      disableEdit: companyPreferences.hasScimDisableEdit,
      groupSync: companyPreferences.hasScimGroupSync,
    }),
    isAutojoinEnabled: companyPreferences.isAutoJoinEnabled,
    isAutoClaimDomainsEnabled: companyPreferences.isAutoClaimDomainsEnabled,
    limited2WaySyncForEmailChannels: companyPreferences.limited2WaySyncForEmailChannels,
    activationPreferences: companyPreferences.activationPreferences,
    enableContactPermissions: companyPreferences.enableContactPermissions,
    csatSurveySettings: serializeCsatSurveySettings(companyPreferences.csatSurveySettings),
    companyEnforcedSignatures: companyPreferences.companyEnforcedSignatures,
    shouldMigrateNewGmailChannels: companyPreferences.shouldMigrateNewGmailChannels,
    selectedChannelGroup: companyPreferences.selectedChannelGroup,
    aiFeatures: companyPreferences.aiSettings,
    shouldShowWorkspaceIndicatorInCell: companyPreferences.shouldShowWorkspaceIndicatorInCell,
    shouldShowInboxIndicatorInCell: companyPreferences.shouldShowInboxIndicatorInCell,
    workspacePermissionsMigrationStatus: companyPreferences.workspacePermissionsMigrationStatus,
  };
}

function serializeCsatSurveySettings(csatSurveySettings?: CompanyPreferencesModel['csatSurveySettings']) {
  if (!csatSurveySettings) {
    return undefined;
  }

  const {companyLogo, ...rest} = csatSurveySettings;

  return {
    ...rest,
    companyLogoId: companyLogo?.cid ?? null,
  };
}

/*
 * API type.
 */

export const ApiCompanyPreferences = makeApiSerializableType(
  'companyPreferences',
  RCompanyPreferences,
  parseCompanyPreferences,
  serializeCompanyPreferences,
);
